import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { APP_NAME, AuthContext, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Clock, Sparkles, InfoIcon } from "lucide-react";
import { toast } from "sonner";

const DashboardHome = () => {
  const navigate = useNavigate();
  const { shop, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(null);
  const intakeUrl = `${window.location.origin}/intake/${shop?.unique_slug}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(intakeUrl);
    toast.success("Link copied to clipboard!");
  };

  // Update page title
  useEffect(() => {
    document.title = `Dashboard - ${APP_NAME}`;
  }, []);

  const goToStripePortal = async () => {
    setLoading('loading');
    try {
      const response = await axios.post(
        `${API}/subscription/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to redirect to Stripe. Please try again.");
      setLoading(null);
    }
  };

  const showTrialBanner = shop?.subscription_status === "trial" && shop?.trial_days_remaining;
  const showPaymentFailureBanner = shop?.subscription_status === "past_due";
  const showUnpaidBanner = shop?.subscription_status === "unpaid" || shop?.subscription_status === "canceled";

  return (
    <div className="w-full max-w-4xl">
      {/* Trial Banner */}
      {showTrialBanner && (
        <div className="bg-gradient-to-r from-[#d4af37]/20 to-[#00ced1]/20 border border-[#d4af37]/40 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]/30">
                <Clock className="w-6 h-6 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#f8f8f8]">
                  {shop.trial_days_remaining} {shop.trial_days_remaining === 1 ? 'Day' : 'Days'} Left in Your Trial
                </h3>
                <p className="text-[#f8f8f8]/70 text-sm">
                  Upgrade now to continue transforming your business experience
                </p>
              </div>
            </div>
            <Button
              data-testid="upgrade-now-btn"
              onClick={() => navigate("/subscription-required")}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0a1a1f] font-semibold"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </div>
      )}

      {/* Payment Failure Banner */}
      {showPaymentFailureBanner && (
        <div className="bg-gradient-to-r from-[#d4af37]/20 to-[#00ced1]/20 border border-[#d4af37]/40 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]/30">
                <InfoIcon className="w-6 h-6 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#f8f8f8]">
                  Payment Failure Notice
                </h3>
                <p className="text-[#f8f8f8]/70 text-sm">
                  Your last payment was failed
                </p>
              </div>
            </div>
            <Button
              data-testid="upgrade-now-btn"
              disabled={loading === "loading"}
              onClick={() => goToStripePortal()}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0a1a1f] font-semibold"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {loading === "loading" ? "Redirecting..." : "Update Payment Method"}
            </Button>
          </div>
        </div>
      )}

      {/* Unpaid Banner */}
      {showUnpaidBanner && (
        <div className="bg-gradient-to-r from-[#d4af37]/20 to-[#00ced1]/20 border border-[#d4af37]/40 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]/30">
                <InfoIcon className="w-6 h-6 text-[#d4af37]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#f8f8f8]">
                  Restriction Notice
                </h3>
                <p className="text-[#f8f8f8]/70 text-sm">
                  Your subscription is restricted due to unpaid payment
                </p>
              </div>
            </div>
            <Button
              data-testid="upgrade-now-btn"
              disabled={loading === "loading"}
              onClick={() => goToStripePortal()}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#0a1a1f] font-semibold"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              {loading === "loading" ? "Redirecting..." : "Update Payment Method"}
            </Button>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#f8f8f8] mb-2">Welcome Back!</h1>
        <p className="text-lg text-[#f8f8f8]/70">Manage your back office and customer intakes</p>
      </div>

      {/* Intake Link Card */}
      <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-2xl border border-[#d4af37]/30 p-8 mb-6">
        <h2 className="text-2xl font-semibold text-[#d4af37] mb-4">Your Customer Intake Link</h2>
        <p className="text-[#f8f8f8]/70 mb-6">
          Share this link with your customers before their appointments. They'll chat with AI to provide all the details you need.
        </p>

        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
          <div className="flex-1 bg-[#0a1a1f]/60 rounded-lg px-4 py-2 border border-[#005f6b]">
            <code className="text-sm text-[#00ced1] font-mono line-clamp-1" data-testid="intake-url">{intakeUrl}</code>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="copy-link-btn"
              onClick={copyToClipboard}
              className="bg-[#00ced1] hover:bg-[#00ced1]/90 text-[#0a1a1f] font-semibold"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button
              data-testid="open-link-btn"
              onClick={() => window.open(intakeUrl, "_blank")}
              variant="outline"
              className="border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37]/10"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-xl border border-[#d4af37]/20 p-6">
          <h3 className="text-sm font-medium text-[#f8f8f8]/60 mb-2">Service Type</h3>
          <p className="text-2xl font-bold text-[#00ced1]">
            {shop?.service_types?.length ? shop.service_types[0].name : "N/A"}
          </p>
          {/* <div className="mt-2 flex flex-wrap gap-1">
            {shop?.service_types?.slice(0, 3).map((type, idx) => (
              <span key={idx} className="text-xs text-[#f8f8f8]/60 bg-[#005f6b]/30 px-2 py-0.5 rounded">
                {type.name}
              </span>
            ))}
            {shop?.service_types?.length > 3 && (
              <span className="text-xs text-[#f8f8f8]/60">+{shop.service_types.length - 3} more</span>
            )}
          </div> */}
        </div>

        <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-xl border border-[#d4af37]/20 p-6">
          <h3 className="text-sm font-medium text-[#f8f8f8]/60 mb-2">Intake URL</h3>
          <p className="text-sm font-semibold text-[#d4af37] break-all">
            /intake/{shop?.unique_slug}
          </p>
        </div>

        <div className="bg-[#005f6b]/20 backdrop-blur-sm rounded-xl border border-[#d4af37]/20 p-6">
          <h3 className="text-sm font-medium text-[#f8f8f8]/60 mb-2">Account Status</h3>
          <p className="text-xl font-semibold text-[#00ced1] capitalize">
            {shop?.subscription_status || "Active"}
          </p>
          {shop?.subscription_status === "trial" && shop?.trial_days_remaining && (
            <p className="text-xs text-[#f8f8f8]/60 mt-1">
              {shop.trial_days_remaining} days remaining
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
